import {
  PageStatusBatchProgressResponse,
  PageStatusGrouped,
  PageStatusResponse,
  PageStatusResponseTimes,
  PageStatusResult as ApiPageStatusResult,
} from '@/declarations/models/PageStatusResponse';
import { RESPONSE_TIME_ERROR_LIMIT_MS, RESPONSE_TIME_WARNING_LIMIT_MS } from './pageStatusConstants';
import { PageStatusResult } from './pageStatusTypes';

export function createEmptyPageStatusGrouped(): PageStatusGrouped {
  return {
    '2xx': [],
    '3xx': [],
    '4xx': [],
    '5xx': [],
    'N/A': [],
  };
}

export const createEmptyPageStatusResponse = (): PageStatusResponse => ({
  summary: {
    total_urls: 0,
    success_count: 0,
    count_300: 0,
    count_400: 0,
    count_500: 0,
    count_na: 0,
    times: {
      elapsed_time_seconds: 0,
      average_response_time_ms: 0,
      median_response_time_ms: 0,
      p90_response_time_ms: 0,
      p95_response_time_ms: 0,
    },
  },
  results: [],
  grouped: createEmptyPageStatusGrouped(),
});

export const createEmptyStatusProgress = (limit: number): PageStatusBatchProgressResponse => ({
  status: 'running',
  offset: 0,
  limit,
  checked: 0,
  total: 0,
  next_offset: 0,
  progress_percent: 0,
  result: createEmptyPageStatusGrouped(),
});

export function mergePageStatusGrouped(current: PageStatusGrouped, incoming: PageStatusGrouped): PageStatusGrouped {
  return {
    '2xx': [...current['2xx'], ...incoming['2xx']],
    '3xx': [...current['3xx'], ...incoming['3xx']],
    '4xx': [...current['4xx'], ...incoming['4xx']],
    '5xx': [...current['5xx'], ...incoming['5xx']],
    'N/A': [...current['N/A'], ...incoming['N/A']],
  };
}

export function getSortedResults(grouped: PageStatusGrouped): ApiPageStatusResult[] {
  return [...grouped['N/A'], ...grouped['5xx'], ...grouped['4xx'], ...grouped['3xx'], ...grouped['2xx']];
}

export function getPercentile(values: number[], percentile: number): number {
  if (values.length === 0) {
    return 0;
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const index = Math.ceil((percentile / 100) * sortedValues.length) - 1;

  return sortedValues[Math.max(0, index)];
}

export function getMedian(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);

  if (sortedValues.length % 2 === 0) {
    return Math.round((sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2);
  }

  return sortedValues[middleIndex];
}

export function createTimesSummary(
  results: ApiPageStatusResult[],
  elapsedTimeSeconds: number,
): PageStatusResponseTimes {
  const responseTimes = results
    .filter((result) => typeof result.status_code === 'number' && typeof result.response_time === 'number')
    .map((result) => result.response_time as number);

  const totalResponseTime = responseTimes.reduce((sum, responseTime) => sum + responseTime, 0);

  return {
    elapsed_time_seconds: elapsedTimeSeconds,
    average_response_time_ms: responseTimes.length > 0 ? Math.round(totalResponseTime / responseTimes.length) : 0,
    median_response_time_ms: getMedian(responseTimes),
    p90_response_time_ms: getPercentile(responseTimes, 90),
    p95_response_time_ms: getPercentile(responseTimes, 95),
  };
}

export function createPageStatusResponseFromGrouped(
  grouped: PageStatusGrouped,
  totalUrls: number,
  checked: number,
  elapsedTimeSeconds: number,
  errorsOnly: boolean,
): PageStatusResponse {
  const results = getSortedResults(grouped);

  const count300 = grouped['3xx'].length;
  const count400 = grouped['4xx'].length;
  const count500 = grouped['5xx'].length;
  const countNa = grouped['N/A'].length;

  const nonSuccessCount = count300 + count400 + count500 + countNa;

  const successCount = errorsOnly ? Math.max(checked - nonSuccessCount, 0) : grouped['2xx'].length;

  return {
    summary: {
      total_urls: totalUrls,
      success_count: successCount,
      count_300: count300,
      count_400: count400,
      count_500: count500,
      count_na: countNa,
      times: createTimesSummary(results, elapsedTimeSeconds),
    },
    results,
    grouped,
  };
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(resolve, ms);

    if (!signal) {
      return;
    }

    if (signal.aborted) {
      window.clearTimeout(timeoutId);
      reject(new DOMException('Polling aborted', 'AbortError'));
      return;
    }

    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeoutId);
        reject(new DOMException('Polling aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

export const getStringValue = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
};

export const getNumericResponseTime = (responseTime: PageStatusResult['response_time']): number | null => {
  if (typeof responseTime === 'number') {
    return responseTime;
  }

  return null;
};

export const getStatusLabel = (statusCode: PageStatusResult['status_code']): string => {
  if (typeof statusCode === 'number') {
    return String(statusCode);
  }

  return statusCode ?? 'N/A';
};

export const isAttentionStatus = (statusCode: PageStatusResult['status_code']): boolean => {
  if (typeof statusCode !== 'number') {
    return true;
  }

  return statusCode < 200 || statusCode > 299;
};

export const getStatusChipColor = (
  statusCode: PageStatusResult['status_code'],
): 'success' | 'warning' | 'error' | 'default' => {
  if (typeof statusCode !== 'number') {
    return 'default';
  }

  if (statusCode >= 200 && statusCode <= 299) {
    return 'success';
  }

  if (statusCode >= 300 && statusCode <= 499) {
    return 'warning';
  }

  if (statusCode >= 500) {
    return 'error';
  }

  return 'default';
};

export const getResponseTimeColor = (responseTime: number): string => {
  if (responseTime >= RESPONSE_TIME_ERROR_LIMIT_MS) {
    return '#EF5350';
  }

  if (responseTime >= RESPONSE_TIME_WARNING_LIMIT_MS) {
    return '#FFA726';
  }

  return '#66BB6A';
};

export const getAttentionCount = (summary: PageStatusResponse['summary']): number =>
  summary.count_300 + summary.count_400 + summary.count_500 + summary.count_na;

export const getStatusSortRank = (statusCode: PageStatusResult['status_code']): number => {
  if (typeof statusCode !== 'number') {
    return 0;
  }

  if (statusCode >= 500) {
    return 1;
  }

  if (statusCode >= 400) {
    return 2;
  }

  if (statusCode >= 300) {
    return 3;
  }

  return 4;
};

export const getAttentionResults = (results: PageStatusResult[]): PageStatusResult[] =>
  [...results]
    .filter((result) => isAttentionStatus(result.status_code))
    .sort((a, b) => {
      const statusRankDiff = getStatusSortRank(a.status_code) - getStatusSortRank(b.status_code);

      if (statusRankDiff !== 0) {
        return statusRankDiff;
      }

      const aResponseTime = getNumericResponseTime(a.response_time) ?? 0;
      const bResponseTime = getNumericResponseTime(b.response_time) ?? 0;

      return bResponseTime - aResponseTime;
    });

export const getSlowestResults = (results: PageStatusResult[], limit = 5): PageStatusResult[] =>
  [...results]
    .filter((result) => getNumericResponseTime(result.response_time) !== null)
    .sort((a, b) => {
      const aResponseTime = getNumericResponseTime(a.response_time) ?? 0;
      const bResponseTime = getNumericResponseTime(b.response_time) ?? 0;

      return bResponseTime - aResponseTime;
    })
    .slice(0, limit);

export const getSortedDetailedResults = (results: PageStatusResult[]): PageStatusResult[] =>
  [...results].sort((a, b) => {
    const statusRankDiff = getStatusSortRank(a.status_code) - getStatusSortRank(b.status_code);

    if (statusRankDiff !== 0) {
      return statusRankDiff;
    }

    const aResponseTime = getNumericResponseTime(a.response_time) ?? 0;
    const bResponseTime = getNumericResponseTime(b.response_time) ?? 0;

    return bResponseTime - aResponseTime;
  });

export const getResponseTimes = (results: PageStatusResult[]): number[] =>
  results
    .map((result) => getNumericResponseTime(result.response_time))
    .filter((responseTime): responseTime is number => responseTime !== null);

export const getMaxResponseTime = (results: PageStatusResult[]): number => {
  const responseTimes = getResponseTimes(results);

  if (responseTimes.length === 0) {
    return 0;
  }

  return Math.max(...responseTimes);
};

export const getSlowResponseCount = (results: PageStatusResult[]): number =>
  getResponseTimes(results).filter((responseTime) => responseTime >= RESPONSE_TIME_WARNING_LIMIT_MS).length;

export const getOriginalUrl = (result: PageStatusResult): string => getStringValue(result.url);

export const getEffectiveUrl = (result: PageStatusResult): string => getStringValue(result.effective_url);

export const cleanDisplayUrl = (url: string): string =>
  url
    .replace(/^https?:\/\//, '')
    .replace(/^localhost:5000\//, '')
    .replace(/^172\.17\.0\.1:5000\//, '');

export const getDisplayUrl = (result: PageStatusResult): string => {
  const originalUrl = getOriginalUrl(result);
  const effectiveUrl = getEffectiveUrl(result);

  return cleanDisplayUrl(originalUrl || effectiveUrl);
};

export const getHrefUrl = (result: PageStatusResult): string | null => {
  const originalUrl = getOriginalUrl(result);
  const effectiveUrl = getEffectiveUrl(result);
  const url = originalUrl || effectiveUrl;

  if (!url) {
    return null;
  }

  if (url.startsWith('http')) {
    return url;
  }

  return `http://${url}`;
};

export const getEffectiveUrlDisplay = (result: PageStatusResult): string => {
  const originalUrl = getOriginalUrl(result);
  const effectiveUrl = getEffectiveUrl(result);

  if (!effectiveUrl || effectiveUrl === originalUrl) {
    return '';
  }

  return cleanDisplayUrl(effectiveUrl);
};
